'use client';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import cn from 'classnames';
import React from 'react';
import { type CvaProps } from '../variants';
import { getSizeByBreakpoints, useMatchScreen } from 'hooks/use-match-screen';

// todo: configure variants so they fit design
const buttonVariants = cva<CvaProps>(
  'inline-flex items-center justify-center gap-[6px] whitespace-nowrap transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-sm rounded-md font-medium text-primary-foreground shadow hover:bg-primary/90',
        defaultRounded:
          'bg-primary text-sm rounded-full font-medium text-primary-foreground shadow hover:bg-primary/90',
        destructive:
          'bg-destructive text-sm rounded-md font-medium text-destructive-foreground shadow-sm hover:bg-destructive/90',
        outline:
          'border border-input text-sm rounded-md font-medium bg-background shadow-sm hover:bg-accent hover:text-accent-foreground',
        secondary:
          'bg-secondary text-sm font-medium rounded-md text-secondary-foreground shadow-sm hover:bg-secondary/80',
        ghost:
          'hover:bg-accent text-sm rounded-md font-medium hover:text-accent-foreground',
        link: 'text-primary rounded-md underline-offset-4 hover:underline',
        action:
          'h-[40px] min-w-[154px] text-[#F9F9F9] font-semibold text-[16px] bg-[#007AFF] bg-gradient-to-t from-[#ffffff00] to-[#ffffff2b] rounded-full hover:opacity-90',
        actionDefault:
          'h-[40px] min-w-[151px] px-[13px] text-[#F9F9F9] font-semibold text-[18px] md:text-[18px] bg-[#007AFF] bg-gradient-to-t from-[#ffffff00] to-[#ffffff2b] rounded-md hover:opacity-90',
        actionGhost:
          'h-[45px] min-w-[154px] bg-[#FFFFFF] font-semibold text-[16px] text-[#1F2125] border-[1px] border-[#EBECEB] rounded-full hover:bg-secondary/80',
      },
      size: {
        m: 'h-9 px-4 py-2',
        s: 'h-8 px-3 text-xs',
        xs: 'h-7 px-3 text-xs',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'm',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  formAction?: (data: FormData) => void;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, formAction, asChild = false, ...props },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';
    const bp = useMatchScreen();
    return (
      <Comp
        className={cn(
          buttonVariants({
            variant,
            size: getSizeByBreakpoints(bp),
            className,
          }),
        )}
        formAction={formAction}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
